import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, ChevronDown } from 'lucide-react';
import { CONTACT_INFO, BRAND_INFO } from '../../data/mock';
import { useCart } from '../../context/CartContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { totalQty } = useCart();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsMenuOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/panels', label: 'Order Labs' },
  ];

  const drawerLinks = [
    { path: '/', label: 'Home' },
    { path: '/panels', label: 'Lab panels' },
    { path: '/biomarkers', label: 'Build à la carte' },
    { path: '/platform', label: 'Our platform' },
    { path: '/how-it-works', label: 'How it works' },
    { path: '/cart', label: 'Cart' },
    { path: '/about', label: 'About us' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
  ];

  const legalLinks = [
    { path: '/terms', label: 'Terms & conditions' },
    { path: '/privacy', label: 'Privacy policy' },
    { path: '/refund', label: 'Refund policy' },
    { path: '/subscription', label: 'Subscription disclosure' },
    { path: '/medical-disclaimer', label: 'Medical disclaimer' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className={`transition-all duration-300 ${isScrolled ? 'bg-cream-100/92 backdrop-blur-md border-b border-cream-200/70 shadow-soft' : 'bg-cream-100'}`}>
        <div className="container-wide">
          <div className="grid grid-cols-3 items-center h-16 lg:h-20">
            {/* Logo — left */}
            <div className="flex items-center justify-start">
              <Link to="/" className="inline-flex items-center gap-2.5 group">
                <span className="logo-mark text-ink-900">
                  <span></span><span></span><span></span><span></span><span></span><span></span>
                </span>
                <span className="font-display text-[1.35rem] lg:text-[1.55rem] text-ink-900 tracking-tight font-medium">
                  {BRAND_INFO.name}
                </span>
              </Link>
            </div>

            {/* Center nav (desktop) */}
            <nav className="hidden lg:flex items-center justify-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-link text-[15px] tracking-tight ${isActive(link.path) ? 'is-active' : ''}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="lg:hidden"></div>

            {/* Right cluster: cart + menu */}
            <div className="flex items-center justify-end gap-1">
              <Link
                to="/cart"
                aria-label={`Cart (${totalQty} items)`}
                className="relative w-10 h-10 inline-flex items-center justify-center rounded-full text-ink-900 hover:bg-cream-200 transition-colors"
              >
                <ShoppingCart className="w-[18px] h-[18px]" />
                {totalQty > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-brick-500 text-white text-[10px] font-semibold tabular-nums">
                    {totalQty}
                  </span>
                )}
              </Link>
              <button
                onClick={() => setIsMobileOpen(true)}
                aria-label="Open menu"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-ink-900 hover:bg-cream-200 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Drawer */}
      <div className={`fixed inset-0 z-40 transition-all duration-300 ${isMobileOpen ? 'visible opacity-100' : 'invisible opacity-0'}`}>
        <div
          className="absolute inset-0 bg-charcoal-950/40 backdrop-blur-sm"
          onClick={() => setIsMobileOpen(false)}
        />
        <aside
          className={`absolute top-0 right-0 bottom-0 w-[88%] max-w-md bg-cream-50 shadow-elevated flex flex-col transform transition-transform duration-300 ${
            isMobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-cream-200">
            <Link to="/" className="inline-flex items-center gap-2.5">
              <span className="logo-mark text-ink-900">
                <span></span><span></span><span></span><span></span><span></span><span></span>
              </span>
              <span className="font-display text-xl text-ink-900">{BRAND_INFO.name}</span>
            </Link>
            <button
              onClick={() => setIsMobileOpen(false)}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-ink-900 hover:bg-cream-200"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-0.5">
              {drawerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`flex items-center justify-between px-4 py-3 rounded-2xl font-display text-lg transition-all ${
                      isActive(link.path) ? 'bg-cream-200 text-ink-900' : 'text-ink-800 hover:bg-cream-100'
                    }`}
                  >
                    <span>{link.label}</span>
                    {link.path === '/cart' && totalQty > 0 && (
                      <span className="min-w-[22px] h-[22px] px-1.5 inline-flex items-center justify-center rounded-full bg-brick-500 text-white text-[11px] font-semibold tabular-nums">
                        {totalQty}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>

            <button
              onClick={() => setIsMenuOpen((v) => !v)}
              className="mt-6 w-full flex items-center justify-between px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-500"
            >
              <span>Legal</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMenuOpen && (
              <ul className="px-1 space-y-0.5">
                {legalLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="block px-4 py-2 rounded-xl text-sm text-ink-700 hover:bg-cream-100"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </nav>

          <div className="p-5 border-t border-cream-200 bg-cream-100 space-y-3 text-sm text-ink-700">
            <Link
              to="/panels"
              className="flex items-center justify-center gap-2 bg-brick-500 hover:bg-brick-600 text-white font-medium px-4 py-3 rounded-full"
            >
              Order labs
            </Link>
            <a
              href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
              className="block text-center text-xs text-ink-600"
            >
              {CONTACT_INFO.phone} · {CONTACT_INFO.email}
            </a>
          </div>
        </aside>
      </div>
    </header>
  );
};

export default Header;
